import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const Section = ({ n, title, children }) => (
  <section className="mb-10" data-testid={`privacy-section-${n}`}>
    <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-500 mb-3">
      {String(n).padStart(2, "0")} — {title}
    </h2>
    <div className="text-zinc-300 text-sm leading-relaxed space-y-3">{children}</div>
  </section>
);

const Privacy = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-zinc-100" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-12"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </Link>

        <header className="mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Legal</div>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight">Privacy Policy</h1>
          <div className="mt-4 text-xs text-zinc-500 font-mono">Last updated: February 21, 2026</div>
        </header>

        <Section n={1} title="Overview">
          <p>
            This Privacy Policy explains how Quill collects, uses, and shares information when you
            use our non-custodial invoicing Service. By using Quill you agree to this Policy.
          </p>
        </Section>

        <Section n={2} title="What we collect">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <span className="text-white">Account data</span> — name, email, profile picture
              (via Google sign-in), business name, country, default currency.
            </li>
            <li>
              <span className="text-white">Invoice data</span> — invoice numbers, line items,
              amounts, client names and emails you enter, due dates, notes.
            </li>
            <li>
              <span className="text-white">Wallet data</span> — public crypto wallet addresses you
              choose to associate with your Quill account. We never collect or store private keys
              or seed phrases.
            </li>
            <li>
              <span className="text-white">Payment confirmations</span> — public on-chain
              transaction hashes and confirmation status fetched from public block explorers.
            </li>
            <li>
              <span className="text-white">Billing data</span> — handled by our third-party
              payments provider. Quill stores a customer/subscription ID and your current tier,
              not card details.
            </li>
            <li>
              <span className="text-white">Usage logs</span> — IP address, user agent, request
              timestamps, basic device info for security and reliability.
            </li>
          </ul>
        </Section>

        <Section n={3} title="What we do NOT collect">
          <p>
            We do not collect private keys, seed phrases, KYC documents, social security numbers,
            or full payment card data. We do not custody funds.
          </p>
        </Section>

        <Section n={4} title="How we use information">
          <ul className="list-disc pl-5 space-y-1">
            <li>To provide, secure, and improve the Service.</li>
            <li>To send transactional emails (invoice receipts, payment confirmations) which you can toggle in Settings.</li>
            <li>To process subscription payments via our billing provider.</li>
            <li>To detect abuse, fraud, and violations of our Terms.</li>
            <li>To comply with legal obligations.</li>
          </ul>
          <p>We do not sell your personal data.</p>
        </Section>

        <Section n={5} title="Sharing">
          <p>We share data with service providers only as needed to run the Service:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Google (authentication)</li>
            <li>Resend (transactional email)</li>
            <li>Our SaaS billing provider (subscription payments)</li>
            <li>Public block explorers (mempool.space, Etherscan, Solscan) — only public wallet addresses and tx hashes</li>
            <li>Cloud infrastructure providers (hosting, database)</li>
          </ul>
          <p>We may also disclose information when required by law or to protect rights and safety.</p>
        </Section>

        <Section n={6} title="On-chain data is public">
          <p>
            Crypto wallet addresses and transactions you process through Quill are recorded on
            public blockchains. Anyone can view this information; Quill cannot delete or anonymize
            on-chain data.
          </p>
        </Section>

        <Section n={7} title="Retention">
          <p>
            We retain account, invoice, and billing data for as long as your account is active and
            for a reasonable period afterwards for legal, tax, and audit purposes. You can request
            deletion of your account by emailing privacy@quillpayed.com.
          </p>
        </Section>

        <Section n={8} title="Your rights">
          <p>
            Depending on your jurisdiction (e.g. GDPR/EEA, UK, California, Brazil), you may have
            the right to access, correct, delete, port, or restrict processing of your personal
            data, and to lodge a complaint with a supervisory authority. To exercise these rights,
            email{" "}
            <a
              href="mailto:privacy@quillpayed.com"
              data-testid="privacy-contact-email"
              className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white"
            >
              privacy@quillpayed.com
            </a>
            .
          </p>
        </Section>

        <Section n={9} title="Cookies & local storage">
          <p>
            Quill uses a session cookie and a local-storage token to keep you signed in. We do not
            use third-party advertising cookies.
          </p>
        </Section>

        <Section n={10} title="Security">
          <p>
            We use industry-standard security controls (HTTPS, encrypted storage, scoped API
            credentials). No system is 100% secure; you use the Service at your own risk and are
            responsible for protecting your wallet credentials.
          </p>
        </Section>

        <Section n={11} title="Children">
          <p>The Service is not directed to anyone under 18, and we do not knowingly collect data from them.</p>
        </Section>

        <Section n={12} title="International transfers">
          <p>
            Your data may be processed in countries other than your own. Where required, we rely
            on standard contractual clauses or equivalent safeguards for cross-border transfers.
          </p>
        </Section>

        <Section n={13} title="GLBA financial privacy notice">
          <p>
            Quill is a non-custodial SaaS invoicing tool. We do not custody customer funds, do not
            transmit money, do not lend, and do not broker financial transactions. Crypto invoice
            payments flow wallet-to-wallet between the creator and their client; Quill never
            touches the funds.
          </p>
          <p>
            We do not believe Quill is a &quot;financial institution&quot; under the
            U.S. Gramm-Leach-Bliley Act (15 U.S.C. §§ 6801–6809) or its implementing
            regulations. Nonetheless, we voluntarily adopt the following GLBA-aligned
            commitments regarding any nonpublic personal information (&quot;NPI&quot;) you provide
            to us:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <span className="text-white">We do not sell NPI.</span> We never sell, rent, or
              license your nonpublic personal information to third parties for their marketing.
            </li>
            <li>
              <span className="text-white">We do not share NPI with non-affiliates for their
              marketing.</span> NPI is shared only with the service providers listed in
              Section&nbsp;5 (authentication, transactional email, billing, hosting, public block
              explorers), strictly to operate the Service.
            </li>
            <li>
              <span className="text-white">No-opt-out promise.</span> Because we don&apos;t share
              NPI with non-affiliates for their marketing in the first place, you have nothing to
              opt out of. The protection is the default.
            </li>
            <li>
              <span className="text-white">Safeguards.</span> We maintain administrative,
              technical, and physical safeguards designed to protect the security and
              confidentiality of your information (see Section&nbsp;10).
            </li>
            <li>
              <span className="text-white">Former customers.</span> These same protections apply
              to information about former customers as long as we retain it.
            </li>
          </ul>
          <p>
            If you have questions about this notice, email{" "}
            <a
              href="mailto:privacy@quillpayed.com"
              className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white"
            >
              privacy@quillpayed.com
            </a>
            .
          </p>
        </Section>

        <Section n={14} title="Changes">
          <p>
            We may update this Policy. Material changes will be communicated through the Service
            or by email. The &quot;Last updated&quot; date above shows the latest revision.
          </p>
        </Section>

        <Section n={15} title="Contact">
          <p>
            For privacy questions, email{" "}
            <a
              href="mailto:privacy@quillpayed.com"
              className="text-white underline underline-offset-4 decoration-zinc-700 hover:decoration-white"
            >
              privacy@quillpayed.com
            </a>
            .
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-zinc-900 flex items-center justify-between text-xs text-zinc-500">
          <Link to="/terms" data-testid="privacy-to-terms-link" className="hover:text-white transition-colors">
            ← Terms of Service
          </Link>
          <div className="font-mono">© 2026 Quill</div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
